
import React, { useState, useEffect, useRef } from 'react';
import { Globe, ChevronDown } from 'lucide-react';
import styles from './Navbar.module.css';

const languages = [
  { code: 'id', label: 'ID', name: 'Indonesia' },
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'ar', label: 'AR', name: 'العربية' },
  { code: 'de', label: 'DE', name: 'Deutsch' },
];

interface GTranslateProps {
  isScrolled?: boolean;
}

const GTranslate: React.FC<GTranslateProps> = ({ isScrolled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('ID');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Menutup dropdown ketika klik di luar area
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode: string, langLabel: string) => {
    setCurrentLang(langLabel);
    setIsOpen(false);
    
    // 1. Metode Cookie: Ini dijamin 100% selalu bekerja, Google otomatis membaca cookie ini.
    // Format cookie "googtrans" selalu: /bahasa-asli/bahasa-tujuan
    const gLang = langCode === 'id' ? '/id/id' : `/id/${langCode}`;
    document.cookie = `googtrans=${gLang}; path=/;`;
    
    // Mencegah cache history dari cookie lokal domain yang berbeda
    if (window.location.hostname !== 'localhost') {
       document.cookie = `googtrans=${gLang}; path=/; domain=${window.location.hostname};`;
    }

    // 2. Coba ubah secara 'seamless' tanpa reload jika combo box berhasil diload oleh Google
    const triggerTranslate = () => {
      // Mencari combobox lewat ID unik atau Tag generic di dalam container google
      const selectArea = document.querySelector('.goog-te-combo') || document.querySelector('#google_translate_element select');
      if (selectArea) {
        (selectArea as HTMLSelectElement).value = langCode === 'id' ? 'id' : langCode; // Bisa id atau kosong
        selectArea.dispatchEvent(new Event('change'));
      } else {
        // Jika Google Translate memblokir inject combobox-nya ke DOM React, paksa Reload.
        // Reload tidak masalah karena cookie sudah tersimpan dan akan langsung memuat bahasa tujuan.
        window.location.reload();
      }
    };

    if (document.querySelector('.goog-te-combo') || document.querySelector('#google_translate_element select')) {
       triggerTranslate();
    } else {
       setTimeout(triggerTranslate, 300); // Tunggu sebentar mencoba peruntungan, jika gagal: auto-reload
    }
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  return (
    <div 
      className={styles.Navbar_navItemWrapper} 
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={styles.Navbar_navLink}
        title="Terjemahkan Halaman / Translate Page"
      >
        <Globe size={16} style={{ marginRight: '6px' }} />
        {currentLang}
        <ChevronDown size={14} style={{ marginLeft: '4px' }} />
      </button>

      {isOpen && (
        <div className={styles.Navbar_dropdown} style={{ minWidth: '160px' }}>
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={(e) => {
                e.stopPropagation();
                handleLanguageChange(lang.code, lang.label);
              }}
              className={styles.Navbar_dropdownItem}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
            >
              <span style={{ fontWeight: currentLang === lang.label ? '700' : '500', color: currentLang === lang.label ? 'var(--color-emerald-700)' : 'inherit' }}>
                {lang.label}
              </span>
              <span style={{ fontSize: '10px', opacity: 0.6, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {lang.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GTranslate;
